import { useEffect } from "react";

const SITE_URL = "https://timbernet.se";
const DEFAULT_TITLE = "TimberNet — Marknadsplats för virke och skogstjänster";
const DEFAULT_DESCRIPTION =
  "TimberNet är Sveriges digitala marknadsplats för virke, ved och skogstjänster. Köp och sälj direkt från skogsägare och leverantörer.";

interface SeoOptions {
  /** Page title. The site suffix " | TimberNet" is appended unless `appendSiteName` is false. */
  title?: string;
  /** Meta description (50–160 characters recommended). */
  description?: string;
  /** Path for the canonical URL (e.g. "/annonser"). Defaults to `window.location.pathname`. */
  path?: string;
  /** Append " | TimberNet" to the title. Default: true. */
  appendSiteName?: boolean;
  /** og:type, default "website". Use "article" / "product" where appropriate. */
  ogType?: string;
}

function setMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Updates document.title, meta description, canonical and og/twitter tags for the current route.
 * Falls back to site defaults when called with no options.
 */
export function useSeo(options: SeoOptions = {}) {
  const {
    title,
    description,
    path,
    appendSiteName = true,
    ogType = "website",
  } = options;

  useEffect(() => {
    const baseTitle = title ?? DEFAULT_TITLE;
    const fullTitle =
      title && appendSiteName && !title.toLowerCase().includes("timbernet")
        ? `${baseTitle} | TimberNet`
        : baseTitle;
    const desc = description ?? DEFAULT_DESCRIPTION;
    const url = `${SITE_URL}${path ?? (typeof window !== "undefined" ? window.location.pathname : "/")}`;

    document.title = fullTitle;
    setMeta('meta[name="description"]', "name", "description", desc);
    setMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    setMeta('meta[property="og:description"]', "property", "og:description", desc);
    setMeta('meta[property="og:url"]', "property", "og:url", url);
    setMeta('meta[property="og:type"]', "property", "og:type", ogType);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", desc);
    setCanonical(url);
  }, [title, description, path, appendSiteName, ogType]);
}
